import type { AppMode } from '@iheartjane/types';
import { configureStore } from '@reduxjs/toolkit';

// eslint-disable-next-line no-restricted-imports
import type { AnalyticsIntegration } from '@jane/shared/models';
import { isStore, setAppMode } from '@jane/shared/util';

import { identifyCustomerUserAtFirstOpportunity } from '../lib/identifyAtFirstOpportunity';
import { setRequestDispatch } from '../lib/request';
import { setNotificationsDispatch } from '../services/notifications';
import { addressAutocompleteReducer } from './redux/addressAutocomplete';
import {
  addGoogleAnalyticsInstance,
  applicationReducer,
} from './redux/application';
import { bundlePossibilitiesReducer } from './redux/bundlePossibilities';
import { cartReducer, persistCartToLocalStorage } from './redux/cart';
import { customerReducer } from './redux/customer';
import { embeddedAppReducer } from './redux/embeddedApp';
import { identificationReducer } from './redux/identification';
import { initialQueryReducer } from './redux/initialQuery';
import { loginReducer } from './redux/login';
import { modalReducer } from './redux/modal';
import { notificationsReducer } from './redux/notifications';
import { operatorEmbeddedReducer } from './redux/operatorEmbedded';
import { posCartReducer } from './redux/posCart';
import { productReducer } from './redux/product';
import { storeReducer } from './redux/store';
import { usersReducer } from './redux/users';

export interface ExtraArgsRef {
  current: { appMode?: AppMode; headlessStoreId?: string };
}

export const configureCustomerStore = (
  preloadedState: object = {},
  extraArgsRef: ExtraArgsRef = {
    current: {},
  }
) => {
  const store = configureStore({
    reducer: {
      addressAutocomplete: addressAutocompleteReducer,
      application: applicationReducer,
      bundlePossibilities: bundlePossibilitiesReducer,
      cart: cartReducer,
      customer: customerReducer,
      embeddedApp: embeddedAppReducer,
      identification: identificationReducer,
      initialQuery: initialQueryReducer,
      login: loginReducer,
      modal: modalReducer,
      notifications: notificationsReducer,
      operatorEmbedded: operatorEmbeddedReducer,
      posCart: posCartReducer,
      product: productReducer,
      store: storeReducer,
      users: usersReducer,
    },
    preloadedState,
    middleware: (getDefaultMiddleware) => {
      /**
       * Certain heavy actions like 'customer/log-in' take longer than the default threshold.
       * @todo [86b7qve4h] Reset to default (32ms)
       */
      const warnAfter = 64;
      return getDefaultMiddleware({
        immutableCheck: {
          warnAfter,
        },
        serializableCheck: {
          warnAfter,
        },
        thunk: {
          extraArgument: extraArgsRef,
        },
      });
    },
  });

  setAppModeForRoutes(store, extraArgsRef.current.appMode);
  setRequestDispatch(store.dispatch);
  setNotificationsDispatch(store.dispatch);
  identifyCustomerUserAtFirstOpportunity(store);
  persistCartToLocalStorage(store, extraArgsRef.current.appMode);

  return store;
};
export type AppStore = ReturnType<typeof configureCustomerStore>;

export type AppState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];

const setAppModeForRoutes = (
  reduxStore: ReturnType<typeof configureCustomerStore>,
  appMode: AppMode
) => {
  reduxStore.subscribe(() => {
    const { operatorEmbedded, embeddedApp, store, application } =
      reduxStore.getState();
    const { partnerId } = embeddedApp;
    const { operatorId } = operatorEmbedded;
    const storeStore = isStore(store.store) ? store.store : undefined;
    let analyticsIntegration: AnalyticsIntegration | undefined;
    if (storeStore?.analytics_integration) {
      analyticsIntegration = { ...storeStore.analytics_integration };
    }

    switch (appMode) {
      case 'default':
        setAppMode({ type: appMode });
        break;
      case 'embedded':
      case 'whiteLabel':
      case 'framelessEmbed':
        if (partnerId != null) {
          setAppMode({ partnerId, type: appMode });
        }
        break;
      case 'operatorEmbed':
        if (operatorId != null && partnerId != null) {
          setAppMode({ operatorId, storeId: partnerId, type: appMode });
        }
        break;
      case 'headless':
        setAppMode({ type: appMode });
        break;
      case 'bloom':
        setAppMode({ type: appMode });
        break;
    }

    if (
      analyticsIntegration &&
      analyticsIntegration.provider_id &&
      analyticsIntegration.enabled &&
      application.googleAnalyticsInstance !==
        analyticsIntegration.provider_id &&
      !['default'].includes(appMode) // we only use GA EC in app modes other than default
    ) {
      reduxStore.dispatch(
        addGoogleAnalyticsInstance({
          trackingId: analyticsIntegration.provider_id,
        })
      );
    }
  });
};
